import { partTypeApi, procurementApi } from '@/main/api';
import KogModalSideComponent from '@/main/components/modal/ModalSide';
import useAsyncModalConfirm from '@/main/components/shared/asyncModalConfirm/asyncModalConfirm';
import Button from '@/main/components/shared/button/button';
import SecondaryButton from '@/main/components/shared/button/secondaryButton';
import LoadingComponent from '@/main/components/shared/loading/loadingComponent';
import ResultNotFoundComponent from '@/main/components/shared/resultNotFound/resultNotFoundComponent';
import { KeyValueDefault } from '@/main/models/common';
import { ReasonFilterRequest } from '@/main/models/request';
import {
  GetItemsCancelledBySupplierResponse,
  GetQuotePartTypesByDirectAssignCriteriaResponse
} from '@/main/models/response';
import {
  buildReassignBodyshopRequest,
  getUnelegibleGuids,
  filterEligibleRequest,
  INITIAL_VALUES_REASSIGN_BODYSHOP_CANCELLED,
  mapPartsToFormValues,
  ReassignBodyshopPartsCancelledForm,
  ReassignBodyshopPartsCancelledFormOutput,
  reassignBodyshopPartsCancelledSchema
} from '@/main/screens/order/cancelledParts/components/modalReassignBodyshopPartsCancelled/modalReassignBodyshopPartsCancelledUtils';
import { ReasonSection } from '@/main/screens/order/cancelledParts/components/modalReassignBodyshopPartsCancelled/reasonSection/reasonSection';
import RowItemReassignBodyshopCancelled from '@/main/screens/order/cancelledParts/components/modalReassignBodyshopPartsCancelled/rowItemReassignBodyshopCancelled/rowItemReassignBodyshopCancelled';
import { UnelegiblePartsConfirmation } from '@/main/screens/order/cancelledParts/components/modalReassignBodyshopPartsCancelled/unelegiblePartsConfirmation/unelegiblePartsConfirmation';
import { ReasonTypeEnum } from '@/main/services/enum/ReasonEnum';
import { isNullUndefined } from '@/main/services/reducerUtils';
import {
  getDefaultCountryOptionDrop,
  getTranslateByKey
} from '@/main/services/utils';
import { getBrand } from '@/main/store/brand';
import { getCountriesUser, useCountryReducer } from '@/main/store/country';
import { getReason } from '@/main/store/reason';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo, useState } from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import * as S from './styles';

const EMPTY_PART_TYPES: KeyValueDefault[] = [];

interface IModalReassignBodyshopPartsCancelled {
  isOpen: boolean;
  quoteGuids: string[];
  procurementSupplierItemGuids: string[];
  handleClose: (refreshGrid: boolean) => void;
  guidCompany: string;
}

function ModalReassignBodyshopPartsCancelledContent({
  guidCompany,
  handleClose,
  isOpen,
  procurementSupplierItemGuids,
  quoteGuids
}: Readonly<IModalReassignBodyshopPartsCancelled>) {
  const [isLoadingGet, setIsLoadingGet] = useState<boolean>(true);
  const [isLoadingSave, setIsLoadingSave] = useState<boolean>(false);
  const [partsData, setPartsData] = useState<
    GetItemsCancelledBySupplierResponse[]
  >([]);
  const [partTypeOptions, setPartTypeOptions] = useState<
    GetQuotePartTypesByDirectAssignCriteriaResponse[]
  >([]);

  const [getConfirmationReassign, ModalConfirmationReassign] =
    useAsyncModalConfirm();
  const [getConfirmationUnelegible, ModalConfirmationUnelegible] =
    useAsyncModalConfirm();

  const { countriesUser: countryReducer } = useCountryReducer();
  const dispatch = useDispatch();

  const methods = useForm<
    ReassignBodyshopPartsCancelledForm,
    any,
    ReassignBodyshopPartsCancelledFormOutput
  >({
    mode: 'onChange',
    resolver: zodResolver(reassignBodyshopPartsCancelledSchema),
    defaultValues: INITIAL_VALUES_REASSIGN_BODYSHOP_CANCELLED
  });

  const { control, handleSubmit, setValue } = methods;

  const { fields: partFields, replace } = useFieldArray({
    control,
    name: 'parts'
  });

  function loadPartTypes() {
    if (isNullUndefined(quoteGuids)) return;
    partTypeApi
      .getPartTypeByDirectAssignCriteriaCancelledParts(quoteGuids)
      .then(res => {
        const {
          data: { result }
        } = res;

        if (result) {
          setPartTypeOptions(result);
        }
      });
  }

  function loadParts() {
    procurementApi
      .getItemsCancelledBySupplierDetails(procurementSupplierItemGuids)
      .then(res => {
        const {
          data: { result }
        } = res;

        if (result) {
          replace(mapPartsToFormValues(result));
          setPartsData(result);
        }
      })
      .finally(() => {
        setIsLoadingGet(false);
      });
  }

  async function onSubmit(data: ReassignBodyshopPartsCancelledFormOutput) {
    const confirmationReassign = await getConfirmationReassign(
      getTranslateByKey('information.confirmReassignBodyshop')
    );
    if (!confirmationReassign) return;
    setIsLoadingSave(true);

    const request = buildReassignBodyshopRequest(data);

    try {
      const response =
        await procurementApi.reassignToBodyshopProcurementSupplierItems(
          request
        );

      const guidsUnelegible = getUnelegibleGuids(response.data.result);

      if (guidsUnelegible.size === 0) {
        handleClose(true);
        toast.success(
          getTranslateByKey('information.reassignCompletedSuccessfully')
        );
        return;
      }

      const partsUnelegible = partsData.filter(p =>
        guidsUnelegible.has(p.procurementSupplierItemGuid)
      );

      const confirmed = await getConfirmationUnelegible(
        <UnelegiblePartsConfirmation parts={partsUnelegible} />
      );
      if (!confirmed) return;

      const newRequest = filterEligibleRequest(request, guidsUnelegible);
      if (newRequest.length === 0) {
        toast.warning(getTranslateByKey('information.noElegiblePartSelected'));
        handleClose(false);
        return;
      }

      await procurementApi.reassignToBodyshopProcurementSupplierItems(
        newRequest
      );
      handleClose(true);
      toast.success(
        getTranslateByKey('information.reassignCompletedSuccessfully')
      );
    } finally {
      setIsLoadingSave(false);
    }
  }

  useEffect(() => {
    if (
      isNullUndefined(quoteGuids) ||
      isNullUndefined(procurementSupplierItemGuids)
    ) {
      setIsLoadingGet(false);
      return;
    }

    loadPartTypes();
    loadParts();

    if (isNullUndefined(countryReducer.data)) dispatch(getCountriesUser());
    dispatch(getBrand());
  }, []);

  useEffect(() => {
    const countryUser = getDefaultCountryOptionDrop(countryReducer.data);
    if (countryUser === null) return;

    const requestReason: ReasonFilterRequest = {
      guidCountry: String(countryUser.key),
      idReasonType: ReasonTypeEnum.RequoteQuote
    };
    dispatch(getReason(requestReason));
  }, [JSON.stringify(countryReducer.data)]);

  const partTypesByQuote = useMemo(
    () => new Map(partTypeOptions.map(p => [p.guidQuote, p.partTypes])),
    [partTypeOptions]
  );

  if (isLoadingGet) return <LoadingComponent />;

  if (partFields.length === 0) return <ResultNotFoundComponent />;

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <S.Container>
          {partFields.map((field, index) => {
            const partData = partsData[index];
            if (!partData) return null;
            return (
              <RowItemReassignBodyshopCancelled
                key={partData.procurementSupplierItemGuid}
                partTypeOptions={
                  partTypesByQuote.get(partData.quoteGuid) ?? EMPTY_PART_TYPES
                }
                partData={partData}
                index={index}
              />
            );
          })}

          <S.AreaReason>
            <ReasonSection control={control} setValue={setValue} />
          </S.AreaReason>

          <S.AreaButtons>
            <SecondaryButton
              id="id-cancelledPartsReassignBodyshop-btn-back"
              type="button"
              onClick={() => handleClose(false)}>
              {getTranslateByKey('information.back')}
            </SecondaryButton>
            <Button
              id="id-cancelledPartsReassignBodyshop-btn-save"
              isLoading={isLoadingSave}
              type="submit">
              {getTranslateByKey('information.confirm')}
            </Button>
          </S.AreaButtons>
        </S.Container>
      </form>
      <ModalConfirmationReassign />
      <ModalConfirmationUnelegible
        confirmLabelKey="information.continueWithElegibleParts"
        cancelLabelKey="information.cancel"
      />
    </FormProvider>
  );
}

export default function ModalReassignBodyshopPartsCancelled(
  props: Readonly<IModalReassignBodyshopPartsCancelled>
) {
  return (
    <KogModalSideComponent
      width="540px"
      title={getTranslateByKey('information.assignPartBodyshop')}
      shouldRenderOpen
      open={props.isOpen}
      closeModal={() => props.handleClose(false)}>
      {props.isOpen && (
        <ModalReassignBodyshopPartsCancelledContent {...props} />
      )}
    </KogModalSideComponent>
  );
}
